import { useTranslations } from "use-intl";
import { Modal } from "@/components/ui/modal/Modal";
import { SHORTCUT_REGISTRY, type ShortcutGroup } from "./shortcut-registry";

interface KeyboardShortcutsCheatsheetProps {
  isOpen: boolean;
  onClose: () => void;
}

const GROUP_ORDER: ShortcutGroup[] = ["global", "navigation", "editing"];

function KbdChip({ label }: { label: string }) {
  return (
    <kbd
      className="inline-flex min-w-[1.5rem] items-center justify-center rounded border border-[var(--border)] bg-[var(--muted)] px-1.5 py-0.5 font-mono text-xs text-[var(--foreground)]"
      aria-hidden="true"
    >
      {label}
    </kbd>
  );
}

/**
 * Modal listing all registered keyboard shortcuts grouped by category.
 * Opened via the "?" shortcut wired in RootLayout.
 */
export function KeyboardShortcutsCheatsheet({
  isOpen,
  onClose,
}: KeyboardShortcutsCheatsheetProps) {
  const t = useTranslations("keyboard_shortcuts");

  const grouped = GROUP_ORDER.map((group) => ({
    group,
    entries: SHORTCUT_REGISTRY.filter((s) => s.group === group),
  })).filter((g) => g.entries.length > 0);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      showCloseButton
      aria-label={t("title")}
    >
      <div className="px-6 pb-6 pt-5">
        <h2 className="mb-4 text-base font-semibold text-[var(--foreground)]">
          {t("title")}
        </h2>
        <div className="space-y-5">
          {grouped.map(({ group, entries }) => (
            <section key={group} aria-labelledby={`shortcut-group-${group}`}>
              <h3
                id={`shortcut-group-${group}`}
                className="mb-2 text-xs font-medium uppercase tracking-wider text-[var(--muted-foreground)]"
              >
                {t(`group_${group}` as Parameters<typeof t>[0])}
              </h3>
              <ul className="space-y-1.5">
                {entries.map((entry) => (
                  <li key={entry.id} className="flex items-center justify-between gap-4">
                    <span className="text-sm text-[var(--foreground)]">
                      {t(entry.labelKey as Parameters<typeof t>[0])}
                    </span>
                    <span className="flex shrink-0 items-center gap-1">
                      {entry.displayKeys.map((k) => (
                        <KbdChip key={k} label={k} />
                      ))}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </div>
    </Modal>
  );
}
